/**
 * Device kind detection.
 *
 * Used to tell the backend whether the user is on a phone or a computer
 * so the AI can frame last-resort asks (browser console, devtools,
 * terminal commands) in a way the user can actually follow.
 *
 * This is a coarse hint, not an identity — the backend treats it as
 * advisory context and always allows the assistant to degrade
 * gracefully when device is unknown.
 */

export type DeviceKind = "mobile" | "desktop";

/**
 * Classify the current environment as mobile or desktop.
 *
 * We treat it as mobile when ALL of these agree it's a touch-first,
 * hover-less, narrow-viewport device — that pattern reliably captures
 * phones without catching touch laptops or tablets in desktop mode.
 *
 * The checks are wrapped in try/catch so a missing matchMedia (old
 * browsers, SSR, test envs) never throws — we fall back to desktop,
 * which is the safer default (no over-restriction on last-resort asks).
 */
export function getDeviceKind(): DeviceKind {
  if (typeof window === "undefined") return "desktop";

  try {
    const mm = window.matchMedia?.bind(window);
    if (!mm) return "desktop";

    const coarse = mm("(pointer: coarse)").matches;
    const noHover = mm("(hover: none)").matches;
    const narrow = mm("(max-width: 768px)").matches;

    // Narrow viewport OR (touch-first AND no hover) → phone.
    // Both signals are required for the touch path so a hybrid
    // laptop with a touchscreen doesn't get mobile framing.
    if (narrow || (coarse && noHover)) return "mobile";
  } catch {
    /* fall through to desktop */
  }

  return "desktop";
}
